import container from '../container/container';

interface PrivateVarsInterface {
	name: string;
	fields: {
		email: {
			[key: string]: EmailFieldObject
		};
		notEqual: {
			[key: string]: NotEqualFieldObject
		};
		required: {
			[key: string]: RequiredFieldObject
		},
		validator: {
			[key: string]: ValidatorFieldObject
		}
	}
}

interface GeneralFieldObject {
	field: string;
	errorField: string;
	errorClass: string;
	errorMessageElement?: string;
	errorMessageShow?: string;
	status: boolean;
}

export interface EmailFieldObject extends GeneralFieldObject {
	defaultValue: string;
	isRequired: boolean;
}

export interface RequiredFieldObject extends GeneralFieldObject {
	defaultValue: string;
}

export interface NotEqualFieldObject extends GeneralFieldObject {
	val1: <T>() => T;
	val2: <T>() => T;
}

export interface ValidatorFieldObject extends GeneralFieldObject {
	defaultValue: string;
	validator: (value: string) => boolean;
}

const formValidator = function (id: number) {

	const privateVars: PrivateVarsInterface = {
		name: 'formValidator(' + id + ')',
		fields: {
			email: {},
			notEqual: {},
			required: {},
			validator: {}
		}
	};


	const privateMethods = {
		prepareObj: function () {
			privateMethods.startup();
			privateMethods.bind();
		},
		startup: function () {

		},
		bind: function () {
			container.msg(privateVars.name, 'module started');
		},
		/*
		 * Performs validation and returns overall validation
		 * status and each field validation status
		 *
		 * @returns { (Boolean) validFlag, (Object) fieldStatus }
		 */
		validate: function () {

			container.msg(privateVars.name, 'start form validation');

			let validFlag = true;

			for (let i in privateVars.fields['required']) {
				validFlag = privateMethods.validateRequiredField(i, validFlag);
			}

			for (let i in privateVars.fields['email']) {
				validFlag = privateMethods.validateEmailField(i, validFlag);
			}

			for (let i in privateVars.fields['notEqual']) {
				validFlag = privateMethods.validateNotEqualField(i, validFlag);
			}

			for (let i in privateVars.fields['validator']) {
				validFlag = privateMethods.validateValidatorField(i, validFlag);
			}

			if (validFlag) {
				container.msg(privateVars.name, 'form is valid!');
			}

			return { validFlag: validFlag, fieldStatus: privateVars.fields };
		},

		validateRequiredField: (key: string, validFlag: boolean): boolean => {
			const fieldHtml = document.querySelector(privateVars.fields['required'][key].field) as HTMLInputElement;

			if(!fieldHtml) {
				container.errorMsg(privateVars.name, 'field not found: ' + privateVars.fields['required'][key].field);
				return false;
			}

			if (!privateMethods.test.required({
				value: fieldHtml.value,
				defaultValue: privateVars.fields['required'][key].defaultValue
			})) {
				validFlag = false;
				privateMethods.error.trigger('required', key, privateVars.fields['required'][key]);
			} else {
				privateMethods.error.clean('required', key, privateVars.fields['required'][key]);
			}

			return validFlag;
		},

		validateEmailField: (key: string, validFlag: boolean): boolean => {
			const fieldHtml = document.querySelector(privateVars.fields['email'][key].field) as HTMLInputElement;

			if(!fieldHtml) {
				container.errorMsg(privateVars.name, 'field not found: ' + privateVars.fields['email'][key].field);
				return false;
			}

			if (!privateMethods.test.email({
				value: fieldHtml.value,
				defaultValue: privateVars.fields['email'][key].defaultValue,
				isRequired: privateVars.fields['email'][key].isRequired
			})) {
				validFlag = false;
				privateMethods.error.trigger('email', key, privateVars.fields['email'][key]);
			} else {
				privateMethods.error.clean('email', key, privateVars.fields['email'][key]);
			}

			return validFlag;
		},

		validateNotEqualField: (key: string, validFlag: boolean): boolean => {
			if (!privateMethods.test.notEqual({
				val1: privateVars.fields['notEqual'][key].val1(),
				val2: privateVars.fields['notEqual'][key].val2()
			})) {
				validFlag = false;
				privateMethods.error.trigger('notEqual', key, privateVars.fields['notEqual'][key]);
			} else {
				privateMethods.error.clean('notEqual', key, privateVars.fields['notEqual'][key]);
			}

			return validFlag;
		},

		validateValidatorField: (key: string, validFlag: boolean): boolean => {
			const fieldHtml = document.querySelector(privateVars.fields['validator'][key].field) as HTMLInputElement;

			if(!fieldHtml) {
				container.errorMsg(privateVars.name, 'field not found: ' + privateVars.fields['validator'][key].field);
				return false;
			}

			if(!privateVars.fields['validator'][key].validator(fieldHtml.value)) {
				validFlag = false;
				privateMethods.error.trigger('validator', key, privateVars.fields['validator'][key]);
			} else {
				privateMethods.error.clean('validator', key, privateVars.fields['validator'][key]);
			}

			return validFlag;
		},

		decorateWithStatus: function<T extends GeneralFieldObject> (obj: { [key: number]: Omit<T, 'status'> }): { [key: number]: T } {

			const newObj: { [key: number]: T } = {};

			for (let i in obj) {
				if(typeof obj[i] !== 'object' || !obj[i]) {
					container.errorMsg(privateVars.name, 'field not an object: ' + i);
					continue;
				}

				newObj[i] = obj[i] as T & { status: boolean };
				newObj[i]['status'] = false;
			}

			return newObj;
		},

		error: {
			/*
			 * trigger error
			 * @param Object field
			 *
			 * {
			 *    errorField: '' // id of error field
			 *    errorClass: '' // class for error field
			 * }
			 *
			 */
			trigger: function (type: string, key: string, field: { field: string, errorField: string, errorMessageElement?: string, errorMessageShow?: string,
				errorClass: string, status: boolean }) {
				const fieldHtml = document.querySelector(field.errorField) as HTMLElement;

				if(!fieldHtml) {
					container.errorMsg(privateVars.name, 'field not found: ' + field.errorField);
					return;
				}

				if(!field.errorField || !field.errorClass) {
					container.errorMsg(privateVars.name, 'error field or class not set');
					return;
				}

				fieldHtml.classList.add(field.errorClass);

				field.status = false;

				if(field.errorMessageElement && field.errorMessageShow) {
					const errorMessageHtml = document.querySelector(field.errorMessageElement) as HTMLElement;

					const errorMessage = fieldHtml.dataset['error' + type];

					if(!errorMessage) {
						container.errorMsg(privateVars.name, 'error message not set: ' + field.errorMessageElement);
						return;
					}

					if(!errorMessageHtml) {
						container.errorMsg(privateVars.name, 'error message field not found: ' + field.errorMessageElement);
						return;
					}

					errorMessageHtml.innerHTML = errorMessage;
					errorMessageHtml.classList.add(field.errorMessageShow)
				}

				container.msg(privateVars.name, 'field error: ' + field.field);
			},
			/*
			 * clean error
			 * @param Object field
			 *
			 * {
			 *    errorField: '' // id of error field
			 *    errorClass: '' // class for error field
			 * }
			 *
			 */
			clean: function (type: string, key: string, field: { field: string, errorField: string, errorMessageElement?: string, errorMessageShow?: string,
				errorClass: string, status: boolean }) {

				field.status = true;

				if(privateVars.fields['required'][key] && !privateVars.fields['required'][key]['status'] && type !== 'required'
					|| privateVars.fields['email'][key] && !privateVars.fields['email'][key]['status'] && type !== 'email'
					|| privateVars.fields['notEqual'][key] && !privateVars.fields['notEqual'][key]['status'] && type !== 'notEqual'
					|| privateVars.fields['validator'][key] && !privateVars.fields['validator'][key]['status'] && type !== 'validator'
				) {
					container.msg(privateVars.name, 'field is not valid clean stopped: ' + field.field);
					return;
				}

				const fieldHtml = document.querySelector(field.errorField) as HTMLElement;

				if(!fieldHtml) {
					container.errorMsg(privateVars.name, 'field not found: ' + field.errorField);
					return;
				}

				if(!field.errorField || !field.errorClass) {
					container.errorMsg(privateVars.name, 'error field or class not set');
					return;
				}

				fieldHtml.classList.remove(field.errorClass);

				if(field.errorMessageElement && field.errorMessageShow) {
					const errorMessageHtml = document.querySelector(field.errorMessageElement) as HTMLElement;

					if(!errorMessageHtml) {
						container.errorMsg(privateVars.name, 'error message field not found: ' + field.errorMessageElement);
						return;
					}

					errorMessageHtml.innerHTML = '';
					errorMessageHtml.classList.remove(field.errorMessageShow)
				}
			}
		},

		setRequiredFields: function (obj: { [key: string]: Omit<RequiredFieldObject, 'status'> }) {
			privateVars.fields['required'] = privateMethods.decorateWithStatus<RequiredFieldObject>(obj);
		},

		setEmailFields: function (obj: { [key: string]: Omit<EmailFieldObject, 'status'> }) {
			privateVars.fields['email'] = privateMethods.decorateWithStatus<EmailFieldObject>(obj);
		},

		setNotEqualFields: function (obj: { [key: string]: Omit<NotEqualFieldObject, 'status'> }) {
			privateVars.fields['notEqual'] = privateMethods.decorateWithStatus<NotEqualFieldObject>(obj);
		},

		setValidatorFields: function (obj: { [key: string]: Omit<ValidatorFieldObject, 'status'> }) {
			privateVars.fields['validator'] = privateMethods.decorateWithStatus<ValidatorFieldObject>(obj);
		},

		test: {
			/*
			 *
			 * Check if required field is empty
			 *
			 * @param Object obj
			 *
			 * {
			 *      value: '', // input field value
			 *      defaultValue: '' // default value or empty string
			 * }
			 *
			 * @returns {Boolean}
			 */
			required: function (obj: { value: string, defaultValue: string }) {
				return !(obj.value === obj.defaultValue || obj.value === '' || !obj.value);
			},
			/*
			 * Check if value is valid email
			 *
			 * @param Object obj
			 *
			 * {
			 *      value: '' // input field value,
			 *      defailtValue: '' // default value or empty string
			 *      isRequired: {Boolean} // if field is required true or false
			 * }
			 *
			 * @returns {Boolean}
			 */
			email: function (obj: { value: string, defaultValue: string, isRequired: boolean }) {


				if (!obj.isRequired && !privateMethods.test.required({
					value: obj.value,
					defaultValue: obj.defaultValue
				})) {
					return true;
				}

				const newReg = new RegExp(/^.+@.+\..+$/);

				return newReg.test(obj.value);
			},
			/*
			 * Check if value is not equal value2
			 *
			 * @param Object obj
			 *
			 * {
			 *      val1: '' // first value,
			 *      val2: '' // second value,
			 * }
			 *
			 * @returns {Boolean}
			 */
			notEqual: function (obj: { val1: boolean | string, val2: boolean | string }) {
				return obj.val1 !== obj.val2;
			}

		},
	};

	return {
		init: function () {
			privateMethods.prepareObj();
		},
		setRequiredFields: privateMethods.setRequiredFields,
		setEmailFields: privateMethods.setEmailFields,
		setNotEqualFields: privateMethods.setNotEqualFields,
		setValidatorFields: privateMethods.setValidatorFields,
		validate: function () {
			return privateMethods.validate();
		},
		validateRequiredField: privateMethods.validateRequiredField,
		validateEmailField: privateMethods.validateEmailField,
		validateNotEqualField: privateMethods.validateNotEqualField,
		validateValidatorField: privateMethods.validateValidatorField
	};
};

const factory = {
	objId: 0,

	create: function () {
		factory.objId++;

		return formValidator(factory.objId);
	}
};

export default {
	create: factory.create
};


/*
 ---
 var formValidator = formValidatorFactory.create();
 ---
 formValidator.set('required', {
 1: {
 field: view.publicVars.cssId.field1requiredId,
 defaultValue: view.publicVars.field1DefaultValue,
 errorField: view.publicVars.cssId.field1requiredId,
 errorClass: view.publicVars.cssId.hasErrorCls.substr(1)
 }
 });
 
 formValidator.set('email', {
 1: {
 field: view.publicVars.cssId.field2emailId,
 defaultValue: '',
 isRequired: true,
 errorField: view.publicVars.cssId.field2emailId,
 errorClass: view.publicVars.cssId.hasErrorCls.substr(1)
 },
 2: {
 field: view.publicVars.cssId.field3emailId,
 defaultValue: '',
 isRequired: false,
 errorField: view.publicVars.cssId.field3emailId,
 errorClass: view.publicVars.cssId.hasErrorCls.substr(1)
 }
 });

 formValidator.set('notequal', {
 1: {
 field: view.publicVars.cssId.field1requiredId,
 errorField: view.publicVars.cssId.field1requiredId,
 errorClass: view.publicVars.cssId.hasErrorCls.substr(1),
 val1: 1,
 val2: 0
 }
 });
 
 formValidator.validate() -> {validFlag: validFlag, fieldStatus: privateVars.fields};
 
 */